import React, { useRef, useState, useEffect, useCallback } from 'react'
import type { RefFile } from './MessageList'
import { ComposerIconActions } from './ComposerIconActions'
import { BusinessSkillsPanel } from './skills/BusinessSkillsPanel'
import { SkillsRail } from './skills/SkillsRail'
import { SkillsSpotlight } from './skills/SkillsSpotlight'
import { useSkills } from '../hooks/useSkills'
import { useMicDictation } from '../hooks/useSpeechRecognition'

const SKILLS_SPOTLIGHT_KEY = 'athena_skills_spotlight_v2'

interface InputBarProps {
  onSend: (text: string, files?: RefFile[], fileToUpload?: File | null) => void
  onInvokeSkill?: (displayMessage: string, skillId: string) => void
}

const InputBar = ({ onSend, onInvokeSkill }: InputBarProps) => {
  const [text, setText] = useState('')
  const [files, setFiles] = useState<RefFile[]>([])
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [skillsOpen, setSkillsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [showSpotlight, setShowSpotlight] = useState(false)
  const [spotlightReady, setSpotlightReady] = useState(false)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const skillsBtnRef = useRef<HTMLButtonElement>(null)
  const skillsRailRef = useRef<HTMLDivElement>(null)
  const textRef = useRef('')
  const { skills } = useSkills()

  textRef.current = text

  const resize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 120) + 'px'
  }

  const applyDictation = useCallback((fullText: string) => {
    setText(fullText)
    requestAnimationFrame(() => {
      if (!taRef.current) return
      taRef.current.focus()
      resize(taRef.current)
    })
  }, [])

  const { isRecording, toggle: toggleMic, stop: stopMic } = useMicDictation({
    getBaseText: () => textRef.current,
    onText: applyDictation,
  })

  useEffect(() => {
    const syncMobile = () => setIsMobile(window.innerWidth < 640)
    syncMobile()
    window.addEventListener('resize', syncMobile)
    return () => window.removeEventListener('resize', syncMobile)
  }, [])

  // One-time FTU after main onboarding (InputBar only mounts once FTUModal is done).
  useEffect(() => {
    if (!onInvokeSkill) return
    let seen = false
    try {
      seen = localStorage.getItem(SKILLS_SPOTLIGHT_KEY) === '1'
    } catch {
      /* ignore */
    }
    if (seen) return
    const t = window.setTimeout(() => setShowSpotlight(true), 650)
    return () => window.clearTimeout(t)
  }, [onInvokeSkill])

  useEffect(() => {
    if (!showSpotlight) {
      setSpotlightReady(false)
      return
    }
    let cancelled = false
    let attempts = 0
    const tryReady = () => {
      if (cancelled) return
      if (skillsRailRef.current) {
        setSpotlightReady(true)
        return
      }
      attempts += 1
      if (attempts < 20) requestAnimationFrame(tryReady)
    }
    const id = requestAnimationFrame(tryReady)
    return () => {
      cancelled = true
      cancelAnimationFrame(id)
    }
  }, [showSpotlight])

  const dismissSpotlight = () => {
    setShowSpotlight(false)
    setSpotlightReady(false)
    try {
      localStorage.setItem(SKILLS_SPOTLIGHT_KEY, '1')
    } catch {
      /* ignore */
    }
  }

  const addFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    const ext = f.name.split('.').pop()?.toLowerCase() ?? ''
    setFiles((prev) => [...prev, { name: f.name, ext }])
    setPendingFile(f)
    e.target.value = ''
  }

  const removeFile = (name: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== name))
    if (pendingFile?.name === name) setPendingFile(null)
  }

  const send = () => {
    const fullText = text.trim()
    if (!fullText && !files.length) return
    if (isRecording) stopMic()
    setSkillsOpen(false)
    onSend(fullText, files, pendingFile)
    setText('')
    setFiles([])
    setPendingFile(null)
    if (taRef.current) taRef.current.style.height = 'auto'
  }

  const invokeSkill = (displayMessage: string, skillId: string) => {
    dismissSpotlight()
    setSkillsOpen(false)
    onInvokeSkill?.(displayMessage, skillId)
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const handleMicClick = () => {
    dismissSpotlight()
    toggleMic()
  }

  return (
    <div
      style={{
        padding: '11px 22px 18px',
        background: 'var(--white)',
        borderTop: '1px solid var(--bd)',
        flexShrink: 0,
        position: 'relative',
        isolation: 'isolate',
      }}
    >
      {skillsOpen && (
        <div
          onClick={() => setSkillsOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,30,56,0.52)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 10,
            animation: 'bIn .3s ease both',
          }}
        />
      )}
      <div
        style={{
          position: 'relative',
          maxWidth: 700,
          margin: '0 auto',
        }}
      >
        {skillsOpen && onInvokeSkill && !isMobile && (
          <BusinessSkillsPanel
            skills={skills}
            isMobile={false}
            onSelect={invokeSkill}
            onClose={() => setSkillsOpen(false)}
            placement="popover"
          />
        )}
        {onInvokeSkill && (
          <SkillsRail
            skills={skills}
            panelOpen={skillsOpen}
            onSelect={invokeSkill}
            onTogglePanel={() => {
              dismissSpotlight()
              setSkillsOpen((open) => !open)
            }}
            skillsBtnRef={skillsBtnRef}
            skillsRailRef={skillsRailRef}
            forceVisible={showSpotlight}
          />
        )}
      </div>
      <div
        style={{
          background: 'var(--bg)',
          border: '1.5px solid var(--bd)',
          borderRadius: 14,
          padding: '11px 13px 9px',
          maxWidth: 700,
          margin: '0 auto',
          transition: 'border-color .25s,box-shadow .25s',
          position: 'relative',
        }}
      >
        {files.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingBottom: 10,
              borderBottom: '1px solid var(--bd2)',
              marginBottom: 8,
              flexWrap: 'wrap',
            }}
          >
            {files.map((f) => (
              <div
                key={f.name}
                className="input-bar-file-chip"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'var(--blue-lt)',
                  border: '1px solid var(--blue-bd)',
                  borderRadius: 6,
                  padding: '4px 10px',
                  fontSize: 11,
                  color: 'var(--blue)',
                }}
              >
                <span>
                  {f.ext === 'pdf'
                    ? '📄'
                    : ['png', 'jpg', 'jpeg'].includes(f.ext)
                      ? '🖼️'
                      : '📊'}
                </span>
                <span
                  style={{
                    fontWeight: 500,
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {f.name}
                </span>
                <span
                  className="input-bar-file-remove"
                  onClick={() => removeFile(f.name)}
                  style={{
                    cursor: 'pointer',
                    color: 'var(--muted)',
                    fontSize: 16,
                    lineHeight: 1,
                    transition: 'color .18s ease',
                  }}
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        )}
        <div style={{ position: 'relative' }}>
          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              resize(e.target)
            }}
            onKeyDown={onKey}
            placeholder={isRecording ? 'Listening… speak now' : 'Ask Athena anything about your business…'}
            rows={1}
            style={{
              width: '100%',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 13,
              color: '#1E293B',
              caretColor: '#1B2A4A',
              resize: 'none',
              lineHeight: 1.65,
              minHeight: 38,
              maxHeight: 120,
              overflowY: 'auto',
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px solid var(--bd2)',
          }}
        >
          <ComposerIconActions
            onFileChange={addFile}
            isRecording={isRecording}
            onMicClick={handleMicClick}
          />
          <button
            type="button"
            onClick={send}
            style={{
              width: 33,
              height: 33,
              borderRadius: 9,
              background: 'var(--navy)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--white)',
              fontSize: 16,
              boxShadow: '0 2px 8px rgba(27,42,74,.2)',
              transition: 'all .2s',
            }}
          >
            ↑
          </button>
        </div>
      </div>
      <div
        style={{
          textAlign: 'center',
          fontSize: 10.5,
          color: 'var(--muted2)',
          paddingTop: 6,
          maxWidth: 700,
          margin: '0 auto',
        }}
      >
        Athena · Responses may be inaccurate. Verify important information with your coach.
      </div>
      {skillsOpen && onInvokeSkill && isMobile && (
        <BusinessSkillsPanel
          skills={skills}
          isMobile
          onSelect={invokeSkill}
          onClose={() => setSkillsOpen(false)}
        />
      )}
      {showSpotlight && spotlightReady && onInvokeSkill && !skillsOpen && (
        <SkillsSpotlight targetEl={skillsRailRef.current} onDismiss={dismissSpotlight} />
      )}
    </div>
  )
}

export default InputBar
